import {
  CalendarOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Alert, Button, Checkbox, InputNumber, Modal, Skeleton } from 'antd';
import { useAxiosRequest, useIsDark } from 'common/hooks';
import BillingModal from 'modules/auth/signUp/BillingModal';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type BillingPeriod = 'Monthly' | 'Yearly';

interface PlanPricing {
  billingPeriod: string;
  pricePerUser: number;
  discountPercent: number;
  effectivePricePerUser: number;
  currencyCode: string;
  currencySymbol: string;
  isDiscountActive: boolean;
  discountValidFrom: string | null;
  discountValidTo: string | null;
  isDiscountForever: boolean;
}

interface PlanLite {
  planId: number;
  planName: string;
  planType: string;
  pricing?: PlanPricing[];
}

interface TrialEmployee {
  employeeId: string;
  name: string;
  email: string;
  department: string;
  designation: string;
  isActive: boolean;
  lastLogin: string | null;
}

interface PreviewData {
  amount: number;
  billType: string;
  proRateDays?: number;
  totalDaysInPeriod?: number;
  fromDate?: string;
  toDate?: string;
  description: string;
}

interface BillData {
  billId: number;
  billAmount: number;
  currency: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
  plan: PlanLite | null;
  billingPeriod: BillingPeriod;
  currentEmployeeCount: number;
}

type Step = 'slots' | 'employees';
type ActionType = 'payNow' | 'payLater';

const formatPrice = (v: number, sym = '$') =>
  Number.isFinite(v) ? `${sym}${v.toFixed(2)}` : `${sym}0.00`;

const formatDate = (iso?: string) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const SelectPlanModal: FC<Props> = ({
  open,
  onClose,
  onCompleted,
  plan,
  billingPeriod,
  currentEmployeeCount,
}) => {
  const { t } = useTranslation();
  const isDark = useIsDark();

  const currentPricing = useMemo(() => {
    if (!plan?.pricing?.length) return null;
    return (
      plan.pricing.find((p) => p.billingPeriod === billingPeriod) ||
      plan.pricing[0]
    );
  }, [plan, billingPeriod]);

  const [step, setStep] = useState<Step>('slots');
  const [employeeSlots, setEmployeeSlots] = useState<number>(
    Math.max(currentEmployeeCount, 1)
  );
  const [employees, setEmployees] = useState<TrialEmployee[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [bill, setBill] = useState<BillData | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [payOpen, setPayOpen] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<ActionType | null>(null);
  const [confirmingAction, setConfirmingAction] = useState<ActionType | null>(null);

  const empReq = useAxiosRequest(null);
  const previewReq = useAxiosRequest(null);
  const selectReq = useAxiosRequest(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep('slots');
    setEmployeeSlots(Math.max(currentEmployeeCount, 1));
    setEmployees([]);
    setSelectedIds([]);
    setPreview(null);
    setBill(null);
    setErrorMsg('');
    setPayOpen(false);
    setPendingAction(null);
    setConfirmingAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan?.planId]);

  // Auto-fetch preview when slots change (debounced), only on slots step
  useEffect(() => {
    if (!open || !plan || step !== 'slots' || employeeSlots < 1) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setErrorMsg('');
      previewReq.apiAction({
        urlObjKey: 'PreviewSelectPlan',
        method: 'POST',
        payload: {
          planId: plan.planId,
          billingPeriod,
          employeeSlots,
          selectedEmployeeIds: [],
        },
        cb: (data: any) => setPreview(data as PreviewData),
        onError: (err: any) => {
          setErrorMsg(
            err?.response?.data?.message ||
            err?.response?.data?.Message ||
            t('manageAccount:modals.selectPlan.errors.previewFailed')
          );
          setPreview(null);
        },
      });
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeSlots, open, plan?.planId, step]);

  const needsEmployeeSelection = employeeSlots < currentEmployeeCount;

  const fetchEmployees = () => {
    empReq.apiAction({
      urlObjKey: 'GetTrialPeriodEmployeeList',
      method: 'GET',
      cb: (data: any) => {
        const list = Array.isArray(data) ? (data as TrialEmployee[]) : [];
        setEmployees(list);
        setSelectedIds((prev) =>
          prev.length
            ? prev.filter((id) => list.some((e) => e.employeeId === id))
            : []
        );
      },
      onError: (err: any) =>
        setErrorMsg(
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          t('manageAccount:modals.selectPlan.errors.employeesFailed')
        ),
    });
  };

  const handleConfirm = (action: ActionType, ids: string[]) => {
    if (!plan) return;
    setConfirmingAction(action);
    setErrorMsg('');
    selectReq.apiAction({
      urlObjKey: 'SelectPlan',
      method: 'POST',
      payload: {
        planId: plan.planId,
        billingPeriod,
        employeeSlots,
        selectedEmployeeIds: needsEmployeeSelection ? ids : [],
      },
      cb: (data: any) => {
        const billData = data as BillData;
        setBill(billData);
        setConfirmingAction(null);
        if (action === 'payNow') {
          setPayOpen(true);
        } else {
          onClose();
          onCompleted?.();
        }
      },
      onError: (err: any) => {
        setConfirmingAction(null);
        setErrorMsg(
          err?.response?.data?.message ||
          err?.response?.data?.Message ||
          t('manageAccount:modals.selectPlan.errors.activateFailed')
        );
      },
    });
  };

  const handleSlotAction = (action: ActionType) => {
    if (!employeeSlots || employeeSlots < 1) {
      setErrorMsg(t('manageAccount:modals.selectPlan.errors.minSlot'));
      return;
    }
    setErrorMsg('');
    if (needsEmployeeSelection) {
      setPendingAction(action);
      setStep('employees');
      if (!employees.length) fetchEmployees();
    } else {
      handleConfirm(action, []);
    }
  };

  const handleEmployeeConfirm = () => {
    if (!pendingAction) return;
    handleConfirm(pendingAction, selectedIds);
  };

  const toggleEmployee = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= employeeSlots) return prev;
      return [...prev, id];
    });
  };

  const handlePayClose = (next: boolean) => {
    setPayOpen(next);
    if (!next) {
      onClose();
      onCompleted?.();
    }
  };

  const handleModalDismiss = () => {
    onClose();
    if (bill) onCompleted?.();
  };

  const title = useMemo(() => {
    if (step === 'employees') return t('manageAccount:modals.selectPlan.pickEmployeesTitle');
    return `${t('manageAccount:modals.selectPlan.activatePrefix')} ${plan?.planName ?? t('manageAccount:modals.selectPlan.fallbackPlan')}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, plan?.planName]);

  if (!plan) return null;

  return (
    <>
      <Modal
        open={open && !payOpen}
        onCancel={handleModalDismiss}
        footer={null}
        destroyOnClose
        maskClosable
        width={560}
        centered
        title={title}
      >
        {errorMsg && (
          <Alert
            type="error"
            message={errorMsg}
            showIcon
            className="mb-4"
            closable
            onClose={() => setErrorMsg('')}
          />
        )}

        {/* ---- STEP 1: slot count + inline preview ---- */}
        {step === 'slots' && (
          <div>
            <p className={`text-sm mb-4 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
              {t('manageAccount:modals.selectPlan.youAreActivating')}{' '}
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                {plan.planName}
              </span>{' '}
              ({billingPeriod}). {t('manageAccount:modals.selectPlan.tellSlots')}
            </p>

            <div className={`border rounded-lg p-4 mb-4 ${isDark ? 'bg-[#0F1E33] border-[#2E3138]' : 'bg-[#F9FAFB] border-[#EAECF0]'}`}>
              <div className={`text-xs mb-2 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                {`${plan?.planName || t('manageAccount:modals.selectPlan.fallbackPlan')} ${t('manageAccount:modals.selectPlan.currentUsageSuffix')}`}
              </div>
              <div className={`flex items-center gap-2 text-sm ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                <TeamOutlined />
                <span className="font-semibold">{currentEmployeeCount}</span>
                <span className={isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}>
                  {t('manageAccount:modals.selectPlan.active')}{' '}
                  {currentEmployeeCount === 1 ? t('manageAccount:modals.selectPlan.employee') : t('manageAccount:modals.selectPlan.employees')}
                </span>
              </div>
            </div>

            <label className={`block text-sm font-semibold mb-1 ${isDark ? 'text-[#CBD5E1]' : 'text-[#344054]'}`}>
              {t('manageAccount:modals.selectPlan.employeeSlots')}
            </label>
            <InputNumber
              size="large"
              className="w-full"
              min={1}
              value={employeeSlots}
              onChange={(v) => setEmployeeSlots(Number(v) || 0)}
              autoFocus
            />
            {needsEmployeeSelection ? (
              <p className={`text-xs mt-2 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                {t('manageAccount:modals.selectPlan.downsizeNotePrefix')} {currentEmployeeCount} {t('manageAccount:modals.selectPlan.downsizeNoteActiveEmployees')}{' '}
                {employeeSlots} {employeeSlots === 1 ? t('manageAccount:modals.selectPlan.slot') : t('manageAccount:modals.selectPlan.slots')}. {t('manageAccount:modals.selectPlan.downsizeNoteSuffix', { employeeSlots })}
              </p>
            ) : (
              <p className={`text-xs mt-2 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                {t('manageAccount:modals.selectPlan.fitsNotePrefix')} {currentEmployeeCount} {t('manageAccount:modals.selectPlan.fitsNoteSuffix')}
              </p>
            )}

            {/* Inline preview */}
            <div className="mt-4">
              {previewReq.isLoading ? (
                <Skeleton active paragraph={{ rows: 3 }} />
              ) : preview ? (
                <div className={`bg-gradient-to-br rounded-xl border p-4 ${isDark ? 'from-blue-900/15 to-indigo-900/15 border-blue-900/40' : 'from-blue-50 to-indigo-50 border-blue-200'}`}>
                  <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                    {preview.billType || t('manageAccount:modals.selectPlan.amountDueDefault')}
                  </div>
                  <div className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                    {formatPrice(preview.amount)}
                  </div>
                  {/* Discount info */}
                  {currentPricing?.isDiscountActive && currentPricing.discountPercent > 0 && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-sm line-through ${isDark ? 'text-[#64748B]' : 'text-gray-400'}`}>
                        {currentPricing.currencySymbol}{currentPricing.pricePerUser.toFixed(2)}
                      </span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isDark ? 'text-emerald-300 bg-emerald-900/30' : 'text-emerald-700 bg-emerald-50'}`}>
                        -{currentPricing.discountPercent}%
                      </span>
                    </div>
                  )}
                  {currentPricing?.isDiscountActive &&
                    !currentPricing.isDiscountForever &&
                    currentPricing.discountValidTo && (
                      <p className={`text-xs mb-2 ${isDark ? 'text-amber-300' : 'text-amber-600'}`}>
                        {t('manageAccount:modals.selectPlan.discountEnds', {
                          date: formatDate(currentPricing.discountValidTo),
                        })}
                      </p>
                    )}
                  {currentPricing?.isDiscountForever && currentPricing.isDiscountActive && (
                    <p className={`text-xs mb-2 ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
                      {currentPricing.discountValidTo
                        ? t('manageAccount:modals.selectPlan.discountForever', {
                            date: formatDate(currentPricing.discountValidTo),
                          })
                        : t('manageAccount:modals.selectPlan.discountForeverOpen')}
                    </p>
                  )}
                  <p className={`text-sm mb-3 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                    {preview.description}
                  </p>
                  <ul className={`space-y-1.5 text-sm ${isDark ? 'text-[#CBD5E1]' : 'text-[#344054]'}`}>
                    {preview.fromDate && preview.toDate && (
                      <li className="flex items-center gap-2">
                        <CalendarOutlined className={isDark ? 'text-[#94A3B8]' : 'text-[#475467]'} />
                        <span>
                          {t('manageAccount:modals.selectPlan.billingWindow')} {formatDate(preview.fromDate)} —{' '}
                          {formatDate(preview.toDate)}
                        </span>
                      </li>
                    )}
                    {typeof preview.proRateDays === 'number' &&
                      typeof preview.totalDaysInPeriod === 'number' && (
                        <li className="flex items-center gap-2">
                          <CalendarOutlined className={isDark ? 'text-[#94A3B8]' : 'text-[#475467]'} />
                          <span>
                            {t('manageAccount:modals.selectPlan.chargedFor', { proRateDays: preview.proRateDays, totalDaysInPeriod: preview.totalDaysInPeriod })}
                          </span>
                        </li>
                      )}
                  </ul>
                  {needsEmployeeSelection && (
                    <p className={`text-xs mt-3 ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                      {t('manageAccount:modals.selectPlan.pickInNextStep', { employeeSlots })}
                    </p>
                  )}
                </div>
              ) : null}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button onClick={onClose}>{t('manageAccount:modals.selectPlan.cancel')}</Button>
              <Button
                type="default"
                onClick={() => handleSlotAction('payLater')}
                loading={confirmingAction === 'payLater' && selectReq.isLoading}
                disabled={employeeSlots < 1 || selectReq.isLoading}
              >
                {t('manageAccount:modals.selectPlan.payLater')}
              </Button>
              <Button
                type="primary"
                onClick={() => handleSlotAction('payNow')}
                loading={confirmingAction === 'payNow' && selectReq.isLoading}
                disabled={employeeSlots < 1 || selectReq.isLoading}
                className="bg-primaryColor"
              >
                {t('manageAccount:modals.selectPlan.payNow')}
              </Button>
            </div>
          </div>
        )}

        {/* ---- STEP 2: choose which employees to keep (downsizing only) ---- */}
        {step === 'employees' && (
          <div>
            <p className={`text-sm mb-3 ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
              {t('manageAccount:modals.selectPlan.pickStepPrefix')}{' '}
              <span className={`font-semibold ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                {employeeSlots}
              </span>{' '}
              {employeeSlots === 1 ? t('manageAccount:modals.selectPlan.employee') : t('manageAccount:modals.selectPlan.employees')} {t('manageAccount:modals.selectPlan.pickStepMiddle')}
            </p>

            {/* Price recap + live selection counter */}
            <div className={`flex items-center justify-between gap-3 border rounded-lg px-4 py-3 mb-3 ${isDark ? 'bg-[#0F1E33] border-[#2E3138]' : 'bg-[#F9FAFB] border-[#EAECF0]'}`}>
              <div className="min-w-0">
                <div className={`text-[11px] uppercase tracking-wider font-semibold ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                  {t('manageAccount:modals.selectPlan.amountDue')}
                </div>
                <div className={`text-lg font-bold leading-tight ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                  {preview ? formatPrice(preview.amount) : '—'}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className={`text-[11px] uppercase tracking-wider font-semibold ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                  {t('manageAccount:modals.selectPlan.selected')}
                </div>
                <div className="text-lg font-bold leading-tight">
                  <span
                    className={
                      selectedIds.length === employeeSlots
                        ? isDark ? 'text-emerald-400' : 'text-emerald-600'
                        : isDark ? 'text-white' : 'text-[#101828]'
                    }
                  >
                    {selectedIds.length}
                  </span>
                  <span className={`text-sm font-normal ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                    {' '}
                    / {employeeSlots}
                  </span>
                </div>
              </div>
            </div>

            {empReq.isLoading && !employees.length ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : employees.length ? (
              <div className={`max-h-[300px] overflow-y-auto custom-scrollbar border rounded-lg divide-y ${isDark ? 'border-[#2E3138] divide-[#2E3138]' : 'border-[#EAECF0] divide-[#EAECF0]'}`}>
                {employees.map((emp) => {
                  const checked = selectedIds.includes(emp.employeeId);
                  const reachedLimit =
                    !checked && selectedIds.length >= employeeSlots;
                  return (
                    <label
                      key={emp.employeeId}
                      className={`flex items-center gap-3 p-3 transition-colors ${reachedLimit
                        ? 'opacity-50 cursor-not-allowed'
                        : isDark
                          ? 'hover:bg-[#0F1E33] cursor-pointer'
                          : 'hover:bg-[#F9FAFB] cursor-pointer'
                        }`}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={reachedLimit}
                        onChange={() => toggleEmployee(emp.employeeId)}
                      />
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#1E293B] text-[#94A3B8]' : 'bg-[#F2F4F7] text-[#475467]'}`}>
                        <UserOutlined />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-[#101828]'}`}>
                          {emp.name || emp.email}
                        </div>
                        <div className={`text-xs truncate ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                          {emp.email}
                          {emp.designation ? ` · ${emp.designation}` : ''}
                          {emp.department ? ` · ${emp.department}` : ''}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className={`py-6 text-center text-sm ${isDark ? 'text-[#94A3B8]' : 'text-[#475467]'}`}>
                {t('manageAccount:modals.selectPlan.noEmployees')}
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button onClick={() => setStep('slots')}>{t('manageAccount:modals.selectPlan.back')}</Button>
              <Button
                type="primary"
                onClick={handleEmployeeConfirm}
                loading={selectReq.isLoading}
                disabled={selectedIds.length !== employeeSlots}
                className="bg-primaryColor"
              >
                {selectReq.isLoading
                  ? t('manageAccount:modals.selectPlan.creatingBill')
                  : pendingAction === 'payNow'
                    ? t('manageAccount:modals.selectPlan.acceptPayNow')
                    : t('manageAccount:modals.selectPlan.acceptPayLater')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {bill && (
        <BillingModal
          billingId={String(bill.billId)}
          ammount={bill.billAmount}
          open={payOpen}
          setOpen={handlePayClose}
        />
      )}
    </>
  );
};

export default SelectPlanModal;
